/**
 * SiteGlue public How It Works — five-step customer journey.
 * Scan → Review → Improve → Activate → Verify. Preserve scanId through Activate.
 */

import {
  AGENT_READY_SCAN_CTA,
  SITEGLUE_DEPLOY_CTA,
  SITEGLUE_PRODUCT_NAME,
} from "~/lib/siteglue/customer-terms";
import { WORDPRESS_PLUGIN_HREF } from "~/lib/siteglue/landing-redesign/copy";

export type HowItWorksStepId = "scan" | "review" | "improve" | "setup" | "verify";

export type HowItWorksStepCta = {
  href: string;
  label: string;
  testId: string;
  kind: "scan" | "review" | "improve" | "setup" | "verify";
};

export type HowItWorksStep = {
  id: HowItWorksStepId;
  n: string;
  label: string;
  heading: string;
  subheading: string;
  lead: string;
  bullets: string[];
  cta: HowItWorksStepCta;
};

export const HOW_IT_WORKS_STEPS: HowItWorksStep[] = [
  {
    id: "scan",
    n: "01",
    label: "Scan",
    heading: "Scan Your Business",
    subheading: "Choose ARO, CRO, or AEO",
    lead: "Enter your website URL. Run Agent Readiness, Conversion, or Answer Engine Optimization. The public scan is free.",
    bullets: [
      "Choose ARO, CRO, or AEO — or start with ARO and CRO from the homepage",
      "See real scores and Not verified items, never a fake 0",
      "No account required to view the report",
    ],
    cta: {
      href: "/agent-ready",
      label: AGENT_READY_SCAN_CTA,
      testId: "button-hiw-scan",
      kind: "scan",
    },
  },
  {
    id: "review",
    n: "02",
    label: "Review",
    heading: "Review Your Audit",
    subheading: "Score, benchmarks, recommendations",
    lead: "Understand your score, benchmark results, and prioritized recommendations before you change anything.",
    bullets: [
      "Independent ARO, CRO, and AEO reports",
      "Each check links to the Audit Benchmark Library",
      "Historical reports keep the catalog version they were scored with",
    ],
    cta: {
      href: "/audits",
      label: "See the audits",
      testId: "button-hiw-review",
      kind: "review",
    },
  },
  {
    id: "improve",
    n: "03",
    label: "Improve",
    heading: "Improve Your Website",
    subheading: "WordPress plugin or platform instructions",
    lead: "For WordPress, install the SiteGlue plugin to implement supported ARO improvements. For other platforms, use install artifacts, coding-agent prompts, or assisted setup. The plugin does not guarantee a higher score or more customers.",
    bullets: [
      "WordPress: SiteGlue plugin can publish llms.txt, MCP discovery, and structured contact facts",
      "Other platforms: copy robots, Link headers, JSON-LD, and Markdown from the install artifacts",
      "Do not share WordPress administrator passwords in agent conversations",
    ],
    cta: {
      href: WORDPRESS_PLUGIN_HREF,
      label: "SiteGlue WordPress plugin",
      testId: "button-hiw-improve",
      kind: "improve",
    },
  },
  {
    id: "setup",
    n: "04",
    label: "Activate",
    heading: "Activate SiteGlue Agent",
    subheading: "Choose the channels you need",
    lead: `Trish configures the ${SITEGLUE_PRODUCT_NAME} from your website, knowledge base, locations, departments, and team routing. She builds SiteGlue Agent — not a separate product.`,
    bullets: [
      "Your scan stays attached when you set up the Agent",
      "Pick AI Agents, Live Chat, Phone + Text, and Email a la carte",
      "AI Agents includes the SiteGlue MCP server and supported business actions",
    ],
    cta: {
      href: "/onboard",
      label: SITEGLUE_DEPLOY_CTA,
      testId: "button-hiw-setup",
      kind: "setup",
    },
  },
  {
    id: "verify",
    n: "05",
    label: "Verify",
    heading: "Verify and Optimize",
    subheading: "Rescan, then monitor connections",
    lead: "Rescan the website, verify improvements, and monitor conversations, connections, and conversions in the dashboard.",
    bullets: [
      "Rescan to confirm supported ARO fixes landed",
      "Inbox and Connection Reporting stay in-app",
      "Moonlight Mode publishes Directory scores only from a ready scan",
    ],
    cta: {
      href: "/directory",
      label: "View the Directory",
      testId: "button-hiw-verify",
      kind: "verify",
    },
  },
];

export const HOW_IT_WORKS_PROGRESS_LABELS = HOW_IT_WORKS_STEPS.map((s) => ({
  id: s.id,
  label: s.label,
}));

/** @deprecated Use HOW_IT_WORKS_STEPS */
export const HOW_ONBOARDING_STAGES = HOW_IT_WORKS_STEPS;
/** @deprecated Use HOW_IT_WORKS_PROGRESS_LABELS */
export const ONBOARDING_PROGRESS_LABELS = HOW_IT_WORKS_PROGRESS_LABELS;
